// Las palabras del módulo Administración (autogestión, plan 2026-10-30
// §1.5), por receta y al estilo de `threadWords`: el menú, las migas y
// los títulos de cada sección las leen de aquí, nunca de un texto fijo en
// el componente. Los sustantivos de la vertical salen de `recipe.labels`
// («Tiendas y zonas», «Unidades y proyectos») y el de quien recibe el
// enlace de `threadWords` («Contratistas»); el resto es
// vocabulario del producto, igual para toda vertical. Sin ramas por slug.
//
// También vive aquí la etiqueta del rol del usuario (antes en
// Sidebar.tsx): la Administración la va a mostrar en Equipo y accesos, y
// es la misma palabra.

import type { AdminSection } from "@bitacorapro/core-engine";
import type { RecipeMeResponse } from "@bitacorapro/api-types";
import { threadWords } from "../postventa/words";

type AdminWordsRecipe = Pick<RecipeMeResponse, "memberRoles" | "labels" | "assetStatuses"> &
  Partial<Pick<RecipeMeResponse, "dashboards">>;

export type AdminWords = {
  /** El módulo: «Administración». */
  module: string;
  /** El título de cada sección, en sentence case. */
  sections: Record<AdminSection, string>;
};

function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

export function adminWords(recipe: AdminWordsRecipe | undefined): AdminWords {
  const assetPlural = recipe?.labels?.assetPlural ?? "Activos";
  const groupPlural = recipe?.labels?.assetGroupPlural ?? "Grupos";
  return {
    module: "Administración",
    sections: {
      resumen: "Resumen",
      empresa: "Empresa",
      equipo: "Equipo y accesos",
      activos: `${assetPlural} y ${lower(groupPlural)}`,
      checklists: "Checklists",
      mantenciones: "Mantenciones",
      contratistas: threadWords(recipe).assigneePlural,
      enlaces: "Enlaces compartidos",
      plazos: "Plazos y avisos",
      registro: "Registro de cambios",
    },
  };
}

// El rol bajo el nombre del usuario, en el vocabulario de la vertical
// (recipe.memberRoles, con los roles de postventa incluidos). El mapa
// fijo cubre lo que la receta no declara (facility no tiene `admin`) y el
// momento antes de que cargue. Los roles legacy de postventa llevan un
// sufijo interno « · legacy» en su label; el cliente nunca lo ve.
const FALLBACK_ROLE_LABELS: Record<string, string> = {
  admin: "Gerente",
  company_manager: "Gerente",
  brand_manager: "Gerente de marca",
  store_manager: "Jefe de tienda",
  facility_supervisor: "Supervisor",
  inspector: "Inspector",
  gerente_postventa: "Gerente de postventa",
  jefe_proyecto: "Jefe de proyecto",
  propietario: "Propietario",
};

export function roleLabelFor(
  role: string | undefined,
  recipe: { memberRoles: ReadonlyArray<{ key: string; label: string }> } | undefined
): string {
  if (!role) return "Sin rol";
  const recipeLabel = recipe?.memberRoles.find((r) => r.key === role)?.label;
  if (recipeLabel) return recipeLabel.replace(/\s*·\s*legacy$/i, "");
  return FALLBACK_ROLE_LABELS[role] ?? "Sin rol";
}
